<script>
    // 허브 격자 '본 콘텐츠' 칸의 채팅 한 장(저작 화면). 사전·사후 자리는 모양이 달라 HubTestSlot 이 그린다.
    //
    // ⚠️ 데이터(p)는 부모 배열의 객체를 그대로 받아 **제자리에서 고친다**(카드 이름·진행 표시).
    //    저장은 부모가 하므로 고칠 때마다 'change' 를 올려 보낸다 — 부모는 그걸로 자동저장만 예약한다.
    import { createEventDispatcher } from "svelte";
    import { Button, Badge, Input, Select } from "../../lib/ui";
    import HubCardIconField from "./HubCardIconField.svelte";

    export let p;
    // 본 콘텐츠 칸의 순번(1부터)
    export let order = null;
    export let canUp = false;
    export let canDown = false;
    // 진행 표시 선택지 — 부모(HubChatPicker DISPLAY)가 서버 문자열 규약과 함께 들고 있다.
    export let displayOptions = [];
    // 허브 기본값의 이름(예: "진행 막대 (8/10 · 80%)"). 카드 값이 비어 있으면(p.kind === "") 이걸 따른다.
    export let defaultLabel = "";

    const dispatch = createEventDispatcher();
</script>

<div class="tw-overflow-hidden tw-rounded-lg tw-border tw-border-solid tw-border-border tw-bg-card tw-shadow-sm">
    <!-- 머리: 순서 · 썸네일 · 제목 · 동작 -->
    <div class="tw-flex tw-items-center tw-gap-3 tw-px-3 tw-py-3">
        {#if order !== null}
            <!-- 순서 번호는 왼쪽 칸으로 뺀다 — 썸네일에 겹치면 이미지에 묻히고,
                 제목 옆에 두면 배지들과 섞여 안 읽힌다. -->
            <span class="tw-flex tw-h-6 tw-w-6 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-foreground tw-text-[11px] tw-font-semibold tw-text-background">
                {order}
            </span>
        {/if}
        <!-- 썸네일: 없어도 같은 자리를 차지해 줄이 흔들리지 않게 한다. -->
        {#if p.thumb}
            <img class="tw-h-11 tw-w-11 tw-shrink-0 tw-rounded-md tw-object-cover" src={p.thumb} alt="" loading="lazy" />
        {:else}
            <div class="tw-flex tw-h-11 tw-w-11 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-md tw-bg-muted tw-text-sm tw-font-semibold tw-text-muted-foreground">
                {(p.title || "?").slice(0, 1)}
            </div>
        {/if}
        <div class="tw-min-w-0 tw-flex-1">
            <div class="tw-flex tw-flex-wrap tw-items-center tw-gap-1.5">
                <span class="tw-max-w-full tw-truncate tw-text-sm tw-font-semibold tw-text-foreground">{p.title}</span>
                {#if !p.published}
                    <!-- 게시본이 없으면 실사용자가 못 연다. 담는 건 되지만 알려는 줘야 한다.
                         게시는 그 채팅의 빌더에서 한다 → 오른쪽 [채팅 빌더]. -->
                    <Badge variant="required">미게시</Badge>
                {/if}
            </div>
            {#if p.subTitle}
                <p class="tw-mb-0 tw-mt-0.5 tw-truncate tw-text-xs tw-text-muted-foreground">{p.subTitle}</p>
            {/if}
        </div>
        <div class="tw-flex tw-shrink-0 tw-items-center tw-gap-1.5">
            <Button variant="outline" size="sm" on:click={() => dispatch("open")}
                    title="이 채팅의 빌더를 새 탭에서 엽니다">
                채팅 빌더
                <svg class="tw-h-3.5 tw-w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                     stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <path d="M15 3h6v6" /><path d="M10 14 21 3" />
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                </svg>
            </Button>
            {#if canUp || canDown}
                <!-- 위/아래는 한 덩어리 버튼으로 붙인다 — 따로 세 개가 늘어서 있으면 무게가 같아 보여
                     '빼기'까지 같은 종류의 동작처럼 읽혔다.
                     움직일 곳이 없으면(칸에 한 장뿐) 아예 안 그린다. -->
                <div class="tw-inline-flex tw-overflow-hidden tw-rounded-md tw-border tw-border-solid tw-border-input">
                    <button type="button" class="tw-flex tw-h-9 tw-w-8 tw-items-center tw-justify-center tw-text-foreground hover:tw-bg-accent disabled:tw-pointer-events-none disabled:tw-opacity-30"
                            title="앞으로" aria-label="앞으로" disabled={!canUp} on:click={() => dispatch("up")}>
                        <svg class="tw-h-4 tw-w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                             stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m18 15-6-6-6 6" /></svg>
                    </button>
                    <span class="tw-w-px tw-bg-input"></span>
                    <button type="button" class="tw-flex tw-h-9 tw-w-8 tw-items-center tw-justify-center tw-text-foreground hover:tw-bg-accent disabled:tw-pointer-events-none disabled:tw-opacity-30"
                            title="뒤로" aria-label="뒤로" disabled={!canDown} on:click={() => dispatch("down")}>
                        <svg class="tw-h-4 tw-w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                             stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m6 9 6 6 6-6" /></svg>
                    </button>
                </div>
            {/if}
            <button type="button" class="tw-flex tw-h-9 tw-w-9 tw-items-center tw-justify-center tw-rounded-md tw-text-muted-foreground hover:tw-bg-destructive/10 hover:tw-text-destructive"
                    title="격자에서 빼기" aria-label="격자에서 빼기" on:click={() => dispatch("remove")}>
                <svg class="tw-h-4 tw-w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                     stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M18 6 6 18" /><path d="m6 6 12 12" /></svg>
            </button>
        </div>
    </div>
    <!-- ⚠️ 구분선을 tw-border-t 로 긋지 않는다 — preflight off 라 border-style 을 같이 줘야 하는데,
            그러면 폭을 안 준 나머지 변이 초기값 medium(3px)로 그려진다. -->
    <div class="tw-h-px tw-bg-border"></div>
    <!-- 발: 카드 한 장 설정(아이콘·이름·진행 표시). -->
    <div class="tw-grid tw-gap-3 tw-bg-muted/30 tw-px-3 tw-py-3 sm:tw-grid-cols-2">
        <!-- 아이콘은 한 줄을 통째로 쓴다 — 상자·버튼·안내가 함께라 이름·진행 표시 칸과 나란히 두면 좁아진다 -->
        <div class="sm:tw-col-span-2">
            <HubCardIconField iconUrl={p.iconUrl} fallbackUrl={p.thumb}
                              on:pick={() => dispatch("pickIcon")} on:clear={() => dispatch("clearIcon")} />
        </div>
        <label class="tw-mb-0 tw-space-y-1 tw-font-normal">
            <span class="tw-block tw-text-xs tw-font-medium tw-text-muted-foreground">카드 이름</span>
            <!-- placeholder 가 곧 '비웠을 때 보일 이름'이다 -->
            <Input type="text" placeholder={p.title || "채팅 제목"}
                   value={p.label}
                   on:change={(e) => { p.label = e.target.value; dispatch("change"); }} />
        </label>
        <label class="tw-mb-0 tw-space-y-1 tw-font-normal">
            <span class="tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-font-medium tw-text-muted-foreground">
                진행 표시
                <!-- 기본값과 다르게 정한 카드만 표시 — 어느 카드가 따로 노는지 목록에서 바로 보이게 -->
                {#if p.kind}<Badge variant="outline">이 카드만</Badge>{/if}
            </span>
            <div class="tw-flex tw-gap-2">
                <!-- 첫 칸 = 허브 기본값 따름(빈 값). 다르게 하고 싶을 때만 아래에서 고른다. -->
                <Select class="tw-min-w-0 tw-flex-1" bind:value={p.kind} on:change={() => dispatch("change")}>
                    <option value="">기본값 따름 — {defaultLabel}</option>
                    {#each displayOptions as d (d.value)}<option value={d.value}>{d.label}</option>{/each}
                </Select>
                {#if p.kind === "metric"}
                    <Input class="tw-w-32" type="text" placeholder="지표 키 (affinity)"
                           value={p.key}
                           on:change={(e) => { p.key = e.target.value; dispatch("change"); }} />
                {/if}
            </div>
        </label>
    </div>
</div>
